import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { BTN_LG } from '../constants/buttons';
import { apiPost } from '../utils/api';
import { FIELD_LABEL_CLASS } from '../constants/inputs';

export default function Login() {
  const { login } = useAuth();

  // Auth pages always render in dark mode
  useEffect(() => {
    document.documentElement.classList.add('dark');
  }, []);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnUrl = searchParams.get('returnUrl') || '/';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setNeedsVerification(false);
    setResent(false);
    setSubmitting(true);
    try {
      const user = await login(email, password);
      // Route a self-signup buyer into the wizard. Two signals — either
      // localStorage (fast-path, set on signup), OR the server-side
      // user.signup_intent column (works across devices / cleared storage /
      // verified-on-different-machine cases). Whichever fires, clear the
      // localStorage flag so it doesn't hijack a future login.
      const lsIntent = localStorage.getItem('sfx_signup_intent');
      const serverIntent = user && user.signup_intent;
      if (lsIntent) localStorage.removeItem('sfx_signup_intent');
      if (
        returnUrl === '/' &&
        (lsIntent === 'create_production' || serverIntent === 'create_production')
      ) {
        navigate('/onboarding/new-production', { replace: true });
      } else {
        navigate(returnUrl, { replace: true });
      }
    } catch (err) {
      // Check if the error response contains needsVerification
      if (err.message.includes('verify your email')) {
        setNeedsVerification(true);
      }
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    setResent(false);
    try {
      await apiPost('/api/auth/resend-verification', { email });
      setResent(true);
    } catch (err) {
      // Silently fail
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
      <div className="w-full max-w-md">
        {/* Logo / Brand */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white" style={{ fontFamily: "'Orbitron', sans-serif" }}>
            CREW FORGE
          </h1>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            Sign in to your account
          </p>
        </div>

        {/* Card */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6 sm:p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-300">
                <p>{error}</p>
                {needsVerification && (
                  <div className="mt-2">
                    {resent ? (
                      <p className="text-green-600 dark:text-green-400">Verification email resent!</p>
                    ) : (
                      <button
                        type="button"
                        onClick={handleResend}
                        disabled={resending}
                        className="text-blue-600 dark:text-blue-400 hover:underline font-medium disabled:opacity-50"
                      >
                        {resending ? 'Sending...' : 'Resend verification email'}
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            <div>
              <label htmlFor="email" className={FIELD_LABEL_CLASS}>
                Email
              </label>
              <input
                id="email"
                type="email"
                required
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                placeholder="you@example.com"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                {/* No `mb-1` here — the wrapping row (`flex items-center justify-between mb-1`)
                    already carries the spacing below this label and the "Forgot password?"
                    link beside it; FIELD_LABEL_CLASS's own `mb-1` would double it. */}
                <label htmlFor="password" className="block text-xs font-medium text-gray-500 dark:text-gray-400">
                  Password
                </label>
                <Link to="/forgot-password" className="text-sm text-blue-600 dark:text-blue-400 hover:underline">
                  Forgot password?
                </Link>
              </div>
              <input
                id="password"
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                placeholder="Enter your password"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className={`${BTN_LG.primary} w-full justify-center`}
            >
              {submitting ? 'Signing in...' : 'Sign in'}
            </button>
          </form>
        </div>

        {/* Sign up link */}
        <p className="mt-6 text-center text-sm text-gray-500 dark:text-gray-400">
          Don't have an account?{' '}
          <Link to="/signup" className="text-blue-600 dark:text-blue-400 font-medium hover:underline">
            Sign up
          </Link>
        </p>

        {/* Legal links */}
        <p className="mt-4 text-center text-xs text-gray-400 dark:text-gray-500">
          <Link to="/privacy-policy" className="hover:underline">Privacy Policy</Link>
          {' · '}
          <Link to="/terms-of-service" className="hover:underline">Terms of Service</Link>
        </p>
      </div>
    </div>
  );
}
